// Google auth state for the dashboard login gate. Holds the current @delricht.com
// identity (hydrated from localStorage), and exposes sign-in acceptance + sign-out.
// The SAME ID token gates API calls too — forwarded as `Authorization: Bearer` by
// installGoogleApiAuth (googleApiAuth.ts). The CRIO token flow stays independent.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  clearIdentity,
  decodeJwt,
  getStoredProfile,
  getStoredToken,
  isAllowedDomain,
  isExpired,
  storeIdentity,
  type GoogleProfile,
} from './googleToken';
import { AUTH_EXPIRED_EVENT } from './googleApiAuth';

interface GoogleAuthState {
  profile: GoogleProfile | null;
  isAuthenticated: boolean;
  /** Accept a fresh Google ID token (JWT credential). Returns null on success, or an error string. */
  acceptCredential: (idToken: string) => string | null;
  signOut: () => void;
}

const GoogleAuthContext = createContext<GoogleAuthState | null>(null);

export const useGoogleAuth = (): GoogleAuthState => {
  const ctx = useContext(GoogleAuthContext);
  if (!ctx) throw new Error('useGoogleAuth must be used within <GoogleAuthProvider>');
  return ctx;
};

export const GoogleAuthProvider: React.FC<{ allowedDomain: string; children: React.ReactNode }> = ({
  allowedDomain,
  children,
}) => {
  // Hydrate from storage: only trust a stored token that is unexpired AND still domain-valid.
  const [profile, setProfile] = useState<GoogleProfile | null>(() => {
    const token = getStoredToken();
    const stored = getStoredProfile();
    if (token && !isExpired(token) && isAllowedDomain(stored, allowedDomain)) return stored;
    if (token) clearIdentity(); // stale/invalid → drop it
    return null;
  });

  const acceptCredential = useCallback(
    (idToken: string): string | null => {
      const payload = decodeJwt(idToken);
      if (!payload) return 'Could not read the Google sign-in response. Please try again.';
      if (isExpired(idToken)) return 'That sign-in has already expired. Please try again.';
      if (!isAllowedDomain(payload, allowedDomain)) {
        return `Access is restricted to @${allowedDomain} accounts. You signed in as ${payload.email || 'an outside account'}.`;
      }
      storeIdentity(idToken, payload);
      setProfile(payload);
      return null;
    },
    [allowedDomain]
  );

  const signOut = useCallback(() => {
    clearIdentity(); // Google keys ONLY
    setProfile(null);
  }, []);

  // When the API auth layer sees an expired/rejected token, drop to the login gate.
  useEffect(() => {
    const onExpired = () => { clearIdentity(); setProfile(null); };
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
  }, []);

  // Proactively drop to the login gate the moment the token expires, so a stale
  // session never lingers (one-click Google re-login resumes it).
  useEffect(() => {
    if (!profile?.exp) return;
    const delay = Math.max(profile.exp * 1000 - Date.now(), 0);
    const timer = window.setTimeout(() => { clearIdentity(); setProfile(null); }, delay);
    return () => window.clearTimeout(timer);
  }, [profile?.exp]);

  const value = useMemo<GoogleAuthState>(
    () => ({ profile, isAuthenticated: !!profile, acceptCredential, signOut }),
    [profile, acceptCredential, signOut]
  );

  return <GoogleAuthContext.Provider value={value}>{children}</GoogleAuthContext.Provider>;
};
